import { Injectable, InjectionToken, inject } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { LOGGER } from '../shared/logger';

/**
 * Persists NgRx feature state, one key per feature named after the feature itself
 * (`settings`, `news`, `push-notification`).
 *
 * Behind a token so single slices can later move to an encrypted backend without
 * touching the effects that use it.
 */
export interface StateStorage {
  load<T>(key: string): Promise<T | null>;
  save<T>(key: string, state: T): Promise<void>;
  clear(key: string): Promise<void>;
}

export const STATE_STORAGE = new InjectionToken<StateStorage>('STATE_STORAGE');

@Injectable()
export class PreferencesStateStorage implements StateStorage {
  private readonly log = inject(LOGGER).scope('storage');

  async load<T>(key: string): Promise<T | null> {
    const { value } = await Preferences.get({ key });
    if (value === null) {
      this.log.debug('nothing stored', { key });
      return null;
    }
    try {
      const parsed = JSON.parse(value) as T;
      this.log.debug('loaded', { key, bytes: value.length });
      return parsed;
    } catch {
      // Unparsable payload is treated as absent, so the caller falls back to defaults
      // instead of failing the app start.
      this.log.warn('dropping unparsable payload', { key, bytes: value.length });
      return null;
    }
  }

  async save<T>(key: string, state: T): Promise<void> {
    const value = JSON.stringify(state);
    await Preferences.set({ key, value });
    this.log.debug('saved', { key, bytes: value.length });
  }

  async clear(key: string): Promise<void> {
    await Preferences.remove({ key });
  }
}
